import { zodResolver } from "@hookform/resolvers/zod";
import { useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useRef } from "react";
import {
  type FieldArrayWithId,
  useFieldArray,
  useForm,
  type UseFormReturn,
} from "react-hook-form";

import { parseFileName } from "@/utils/getExtensionFromMime";

import { useGetRequestMyFiles } from "../../../hooks/useGetRequestMyFiles";
import {
  type FileFormData,
  type UploadFilesFormData,
  uploadFilesFormSchema,
} from "../schema";
import { useMultipartUploadMutations } from "./useMultipartUploadMutations";
import { useUploadQueue } from "./useUploadQueue";

const getChunkSize = (fileSize: number) => {
  if (fileSize > 5 * 1024 * 1024 * 1024) return 100 * 1024 * 1024; // 100MB
  if (fileSize > 1024 * 1024 * 1024) return 50 * 1024 * 1024; // 50MB

  return 25 * 1024 * 1024; // Default for smaller files
};

const SOURCE_FILE_EXTENSIONS = [
  "m4v",
  "psd",
  "psb",
  "ai",
  "indd",
  "prproj",
  "aep",
  "fig",
  "ttf",
  "otf",
] as const;

export type FileUploadStatus = "error" | "uploaded" | "uploading";

export function useUploadFiles(
  formMethods: UseFormReturn<UploadFilesFormData>,
): {
  addFiles: (fileList: FileList | null) => void;
  cancelUpload: (formIndex: number) => void;
  deleteAllFiles: () => void;
  deleteFile: (formIndex: number) => void;
  fields: FieldArrayWithId<UploadFilesFormData, "files", "fieldId">[];
  isUploading: boolean;
  uploadingCount: number;
} {
  const { requestId } = useSearch({ strict: false });
  const { control, getValues, setValue, trigger, watch } = formMethods;
  const { append, fields, remove } = useFieldArray({
    control,
    keyName: "fieldId",
    name: "files",
  });

  const { data: myFiles } = useGetRequestMyFiles();
  const formFiles = watch("files");

  const [queueState, dispatchQueue] = useUploadQueue();

  const abortControllers = useRef<Map<number, AbortController>>(new Map());

  const {
    abortMultipartUploadMutation,
    completeMultipartUploadMutation,
    getPresignedUrlsForPartsMutation,
    initiateMultipartUploadMutation,
    uploadChunkMutation,
  } = useMultipartUploadMutations();

  const uploadFileWithMultipart = async ({
    abortController,
    file,
    formIndex,
  }: {
    abortController: AbortController;
    file: File;
    formIndex: number;
  }): Promise<number> => {
    const { attachmentId, key, uploadId } =
      await initiateMultipartUploadMutation.mutateAsync({
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
        requestId: requestId!,
      });

    // Store upload metadata in form
    setValue(`files.${formIndex}.uploadId`, uploadId);
    setValue(`files.${formIndex}.key`, key);
    setValue(`files.${formIndex}.uploadProgress`, 0);

    // 2. Calculate parts with dynamic chunk size
    const chunkSize = getChunkSize(file.size);
    const totalParts = Math.ceil(file.size / chunkSize);

    setValue(`files.${formIndex}.totalParts`, totalParts);
    setValue(`files.${formIndex}.uploadedParts`, 0);

    const uploadedParts: { ETag: string; PartNumber: number }[] = [];
    let completedParts = 0;

    try {
      // 3. Setup on-demand presigned URL fetching
      const BATCH_SIZE = 10; // Request 10 presigned URLs at a time when needed
      const presignedUrlMap = new Map<number, string>();
      let nextBatchStart = 1;

      // Function to fetch next batch of presigned URLs when needed
      const fetchNextBatch = async (): Promise<void> => {
        if (nextBatchStart > totalParts) return;

        const batchEnd = Math.min(nextBatchStart + BATCH_SIZE - 1, totalParts);

        const { presignedUrls } =
          await getPresignedUrlsForPartsMutation.mutateAsync({
            endPart: batchEnd,
            key,
            startPart: nextBatchStart,
            uploadId,
          });

        // Store presigned URLs in map for quick lookup
        for (const { partNumber, presignedUrl } of presignedUrls) {
          presignedUrlMap.set(partNumber, presignedUrl);
        }

        nextBatchStart = batchEnd + 1;
      };

      // Fetch initial batch
      await fetchNextBatch();

      // 4. Upload parts with concurrency limit of 3
      const MAX_CONCURRENT_UPLOADS = 3;

      let totalBytesUploaded = 0;
      const progressLock = { updating: false };

      // Function to safely update progress
      const updateProgress = (
        bytesUploaded: number,
        totalBytes: number = file.size,
      ) => {
        if (progressLock.updating) return;
        progressLock.updating = true;

        const progress = Math.round((bytesUploaded / totalBytes) * 100);

        setValue(`files.${formIndex}.uploadProgress`, progress);

        progressLock.updating = false;
      };

      const uploadPart = async (partNumber: number): Promise<void> => {
        if (abortController.signal.aborted) {
          throw new Error("Upload cancelled");
        }

        const start = (partNumber - 1) * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const chunk = file.slice(start, end);

        // Get presigned URL logic...
        let presignedUrl = presignedUrlMap.get(partNumber);

        if (!presignedUrl) {
          await fetchNextBatch();
          presignedUrl = presignedUrlMap.get(partNumber);
          if (!presignedUrl) {
            throw new Error(`No presigned URL found for part ${partNumber}`);
          }
        }

        let partBytesUploaded = 0;

        const response = await uploadChunkMutation.mutateAsync({
          chunk,
          onUploadProgress: (progressEvent) => {
            if (progressEvent.loaded && progressEvent.total) {
              // Calculate the delta for this progress event
              const newPartBytes = progressEvent.loaded;
              const deltaBytes = newPartBytes - partBytesUploaded;

              // Atomically update total progress
              totalBytesUploaded += deltaBytes;
              partBytesUploaded = newPartBytes;

              updateProgress(totalBytesUploaded);
            }
          },
          presignedUrl,
          signal: abortController.signal,
        });

        if (response.status < 200 || response.status >= 300) {
          throw new Error(
            `Failed to upload part ${partNumber}: ${response.statusText}`,
          );
        }

        const etag = response.headers.etag || response.headers.ETag;

        if (!etag) {
          throw new Error(`No ETag received for part ${partNumber}`);
        }

        uploadedParts.push({
          ETag: etag,
          PartNumber: partNumber,
        });

        // Update completed parts count atomically
        completedParts++;
        setValue(`files.${formIndex}.uploadedParts`, completedParts);
      };

      // Upload all parts with concurrency control
      const uploadPromises: Promise<void>[] = [];
      let currentPart = 1;

      // Function to start next upload when a slot becomes available
      const startNextUpload = async (): Promise<void> => {
        if (currentPart > totalParts) return;

        const partToUpload = currentPart++;

        await uploadPart(partToUpload);

        // Start next upload if there are more parts
        if (currentPart <= totalParts) {
          await startNextUpload();
        }
      };

      // Start initial concurrent uploads (up to MAX_CONCURRENT_UPLOADS)
      for (let i = 0; i < Math.min(MAX_CONCURRENT_UPLOADS, totalParts); i++) {
        uploadPromises.push(startNextUpload());
      }

      // Wait for all uploads to complete
      await Promise.all(uploadPromises);

      // Sort parts by part number for completion
      uploadedParts.sort((a, b) => a.PartNumber - b.PartNumber);

      // 4. Complete multipart upload
      await completeMultipartUploadMutation.mutateAsync({
        attachmentId,
        key,
        parts: uploadedParts,
        uploadId,
      });

      return attachmentId;
    } catch (error) {
      // Abort the multipart upload on error
      try {
        await abortMultipartUploadMutation.mutateAsync({
          attachmentId,
          key,
          uploadId,
        });
      } catch (abortError) {
        console.error("Failed to abort multipart upload:", abortError);
      }
      throw error;
    }
  };

  useEffect(() => {
    if (
      queueState.currentlyUploading === null &&
      queueState.uploadQueue.length > 0
    ) {
      const nextUploadItem = queueState.uploadQueue[0];

      if (!nextUploadItem) {
        return;
      }

      const { file, formIndex } = nextUploadItem;

      const formFileExists = getValues("files").some(
        (_f, idx) => idx === formIndex,
      );

      if (!formFileExists) {
        dispatchQueue({ payload: formIndex, type: "REMOVE_FROM_QUEUE" });

        return;
      }

      const startUpload = async () => {
        dispatchQueue({ payload: formIndex, type: "START_UPLOAD" });
        setValue(`files.${formIndex}.status`, "uploading", {
          shouldDirty: true,
        });
        setValue(`files.${formIndex}.errorMessage`, undefined);

        const abortController = new AbortController();

        abortControllers.current.set(formIndex, abortController);

        let attachmentId;

        try {
          // Use multipart upload for all files
          attachmentId = await uploadFileWithMultipart({
            abortController,
            file,
            formIndex,
          });
          setValue(`files.${formIndex}.status`, "uploaded", {
            shouldDirty: true,
          });

          setValue(`files.${formIndex}.attachmentId`, attachmentId, {
            shouldDirty: true,
          });
          setValue(`files.${formIndex}.errorMessage`, undefined);
        } catch (error: unknown) {
          console.error(
            `[Upload ${formIndex}] Error during upload process:`,
            error,
          );
          let errorIndex = formIndex;
          let errorMessage = "An unknown upload error occurred";
          let isAbortError = false;

          if (error instanceof Error) {
            errorMessage = error.message;
            isAbortError = error.name === "AbortError";
            if (
              typeof error === "object" &&
              error !== null &&
              "formIndex" in error &&
              typeof (error as { formIndex: unknown }).formIndex === "number"
            ) {
              errorIndex = (error as { formIndex: number }).formIndex;
            }
          }

          const currentFiles = getValues("files");

          if (currentFiles.some((_f, idx) => idx === errorIndex)) {
            if (isAbortError) {
              const fileAtIndex = getValues(`files.${errorIndex}`);

              if (fileAtIndex && fileAtIndex.status === "uploading") {
                setValue(`files.${errorIndex}.status`, "error", {
                  shouldDirty: true,
                });
                setValue(
                  `files.${errorIndex}.errorMessage`,
                  "Upload cancelled",
                );
              }
            } else {
              setValue(`files.${errorIndex}.status`, "error", {
                shouldDirty: true,
              });
              setValue(`files.${errorIndex}.errorMessage`, errorMessage);
            }
          }
        } finally {
          abortControllers.current.delete(formIndex);
          dispatchQueue({ payload: formIndex, type: "FINISH_UPLOAD" });
        }
      };

      startUpload();
    }
  }, [
    queueState.currentlyUploading,
    queueState.uploadQueue,
    getValues,
    setValue,
  ]);

  const addFiles = useCallback(
    (fileList: FileList | null) => {
      if (!fileList) return;

      const filesToAdd = [...fileList];

      for (const file of filesToAdd) {
        let fileExtension: string | undefined;
        let fileNameWithoutExtension: string;

        try {
          const { baseName, ext } = parseFileName(file.name);

          fileExtension = ext;
          fileNameWithoutExtension = baseName;
        } catch {
          // Fallback for invalid filenames
          fileExtension = file.name.split(".").pop()?.toLowerCase();
          fileNameWithoutExtension = file.name
            .split(".")
            .slice(0, -1)
            .join(".") as string;
        }

        let isSourceFile = fileExtension
          ? SOURCE_FILE_EXTENSIONS.includes(
              fileExtension as (typeof SOURCE_FILE_EXTENSIONS)[number],
            )
          : false;
        let previousVersionId = null;

        const matchedFile = myFiles?.find(
          (existingFile: {
            id: number;
            isSourceFile: boolean;
            name: string;
          }) => {
            try {
              // Extract basename and extension from existing file
              const { baseName: existingBaseName, ext: existingExt } =
                parseFileName(existingFile.name);

              // Match if both basename and extension are the same
              return (
                existingBaseName === fileNameWithoutExtension &&
                existingExt === fileExtension
              );
            } catch {
              // Fallback to simple comparison if parsing fails
              return false;
            }
          },
        );

        if (matchedFile) {
          previousVersionId = matchedFile.id;
          isSourceFile = matchedFile.isSourceFile;
        }

        const newFileFormData: FileFormData = {
          file: file,
          id: crypto.randomUUID(),
          isSourceFile,
          name: fileNameWithoutExtension,
          originalName: file.name,
          previousVersionId: previousVersionId,
          status: "uploading",
        };

        append(newFileFormData, { shouldFocus: false });

        const newIndex = getValues("files").length - 1;

        dispatchQueue({
          payload: { file, formIndex: newIndex },
          type: "ADD_TO_QUEUE",
        });

        trigger(`files.${newIndex}.name`);
      }
    },
    [append, dispatchQueue, getValues, trigger, myFiles],
  );

  const cancelUpload = useCallback(
    (formIndex: number) => {
      const controller = abortControllers.current.get(formIndex);

      if (controller) {
        controller.abort();
        abortControllers.current.delete(formIndex);
      }

      dispatchQueue({ payload: formIndex, type: "REMOVE_FROM_QUEUE" });
      remove(formIndex);
    },
    [remove],
  );

  const deleteFile = useCallback(
    (formIndex: number) => {
      const controller = abortControllers.current.get(formIndex);

      if (controller) {
        controller.abort();
        abortControllers.current.delete(formIndex);
      }
      dispatchQueue({ payload: formIndex, type: "REMOVE_FROM_QUEUE" });
      remove(formIndex);

      // Trigger validation after removing a file to clear errors
      setTimeout(() => {
        trigger("files");
      }, 0);
    },
    [remove, trigger],
  );

  const deleteAllFiles = useCallback(() => {
    for (const [, controller] of abortControllers.current.entries()) {
      controller.abort();
    }
    abortControllers.current.clear();
    dispatchQueue({ type: "CLEAR_QUEUE" });

    // Remove all files and clear form errors
    setValue("files", []);

    // Trigger validation to clear any remaining errors
    setTimeout(() => {
      trigger("files");
    }, 0);
  }, [dispatchQueue, setValue, trigger]);

  const uploadingCount =
    formFiles?.filter((f) => f?.status === "uploading").length ?? 0;
  const isUploading =
    queueState.currentlyUploading !== null || uploadingCount > 0;

  return {
    addFiles,
    cancelUpload,
    deleteAllFiles,
    deleteFile,
    fields,
    isUploading,
    uploadingCount,
  };
}

export const useUploadFilesForm = (): UseFormReturn<UploadFilesFormData> =>
  useForm<UploadFilesFormData>({
    defaultValues: {
      files: [],
    },
    mode: "onChange",
    resolver: zodResolver(uploadFilesFormSchema),
  });
